import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import client, { apiError } from './adminApi';
import { useAdminAuth } from './AdminAuth';

const inputCls = 'mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900';

// Global site settings — single source for contact details, socials and
// default SEO. Shared components read these via the public bootstrap API.
export default function SettingsPage() {
  const { user } = useAdminAuth();
  const [settings, setSettings] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = ['owner', 'administrator'].includes(user.role);

  useEffect(() => {
    client.get('/admin/settings').then((res) => setSettings(res.data.settings || {})).catch((err) => setError(apiError(err)));
  }, []);

  if (!settings) return <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>;

  const contact = settings.contact || {};
  const setContact = (k, v) => setSettings({ ...settings, contact: { ...contact, [k]: v } });

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await client.put('/admin/settings', settings);
      setNotice('Settings saved — shared components update globally.');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const fields = [
    ['businessName', 'Business name', settings.businessName, (v) => setSettings({ ...settings, businessName: v })],
    ['tagline', 'Tagline', settings.tagline, (v) => setSettings({ ...settings, tagline: v })],
    ['phone', 'Phone (display)', contact.phone, (v) => setContact('phone', v)],
    ['phoneHref', 'Phone (tel: link)', contact.phoneHref, (v) => setContact('phoneHref', v)],
    ['email', 'Email', contact.email, (v) => setContact('email', v)],
    ['emailHref', 'Email (mailto: link)', contact.emailHref, (v) => setContact('emailHref', v)],
    ['enquiryRecipient', 'Enquiry recipient email', settings.enquiryRecipient, (v) => setSettings({ ...settings, enquiryRecipient: v })],
    ['bookingNotificationRecipient', 'Booking notification email', settings.bookingNotificationRecipient, (v) => setSettings({ ...settings, bookingNotificationRecipient: v })],
    ['defaultSeoTitle', 'Default SEO title', settings.defaultSeoTitle, (v) => setSettings({ ...settings, defaultSeoTitle: v })],
    ['defaultMetaDescription', 'Default meta description', settings.defaultMetaDescription, (v) => setSettings({ ...settings, defaultMetaDescription: v })],
    ['bookingTimezone', 'Booking timezone', settings.bookingTimezone, (v) => setSettings({ ...settings, bookingTimezone: v })],
    ['privacyContact', 'Privacy contact email', settings.privacyContact, (v) => setSettings({ ...settings, privacyContact: v })],
  ];

  return (
    <div className="max-w-3xl" data-testid="admin-settings">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Site Settings</h1>
      <p className="mt-1 text-sm text-slate-500">Verified global information used across the website.</p>

      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        {fields.map(([key, label, value, setter]) => (
          <div key={key}>
            <label htmlFor={`set-${key}`} className="text-sm font-bold text-navy-900">{label}</label>
            <input id={`set-${key}`} value={value || ''} onChange={(e) => setter(e.target.value)} disabled={!canEdit} data-testid={`set-${key}`} className={inputCls} />
          </div>
        ))}
        <div>
          <label htmlFor="set-address" className="text-sm font-bold text-navy-900">Office address</label>
          <textarea id="set-address" rows={2} value={contact.address || ''} onChange={(e) => setContact('address', e.target.value)} disabled={!canEdit} data-testid="set-address" className={inputCls} />
        </div>
        <div>
          <label htmlFor="set-socials" className="text-sm font-bold text-navy-900">Social links (one per line: label | url)</label>
          <textarea
            id="set-socials" rows={3}
            value={(settings.socials || []).map((s) => `${s.label} | ${s.href}`).join('\n')}
            onChange={(e) => setSettings({
              ...settings,
              socials: e.target.value.split('\n').map((line) => {
                const [label, href] = line.split('|').map((s) => (s || '').trim());
                return label && href ? { label, href, icon: label.toLowerCase().includes('linkedin') ? 'linkedin' : 'link' } : null;
              }).filter(Boolean),
            })}
            disabled={!canEdit}
            data-testid="set-socials"
            className={inputCls}
          />
        </div>
      </div>

      {notice && <p role="status" className="mt-4 rounded-md border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-teal-800" data-testid="settings-notice">{notice}</p>}
      {error && <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700">{error}</p>}

      {canEdit && (
        <button type="button" onClick={save} disabled={busy} data-testid="settings-save" className="mt-5 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white hover:bg-navy-700 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save Settings
        </button>
      )}
    </div>
  );
}
